import { AlertTriangle } from "lucide-react";

import type { EffectiveSummary } from "@/modules/dashboard/settings/types/access.types";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";

import type { UserAssignedRole } from "../types/users-api.types";

export interface UserAccessCellProps {
  roles: readonly UserAssignedRole[];
  summary: EffectiveSummary | undefined;
  /** The role catalog could not be read, so neither the roles nor the tally can be trusted. */
  unavailable: boolean;
  onOpen: () => void;
}

/**
 * What this person can do — and the one cell in the row that is directly actionable, because access is a
 * first-class property here rather than a field hidden behind an edit form.
 *
 * Three states, and the third is the one that matters: roles that could not be READ render as "no
 * disponibles", never as "sin roles". Those are opposite claims, and collapsing them would tell an operator
 * that someone has no access when the truth is that we do not know.
 */
export function UserAccessCell({ roles, summary, unavailable, onOpen }: UserAccessCellProps) {
  const { t } = useTranslations("users");

  if (unavailable) {
    return <span className="text-xs italic text-muted-foreground">{t("access_unavailable")}</span>;
  }

  if (roles.length === 0) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <AlertTriangle className="size-3.5 shrink-0" aria-hidden="true" />
        {t("access_none")}
      </button>
    );
  }

  const [first, ...rest] = roles;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex max-w-full flex-col items-start gap-1 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="flex max-w-full items-center gap-1">
        <ColocaBadge variant="secondary" className="max-w-[130px] truncate">
          {first?.name}
        </ColocaBadge>
        {rest.length > 0 && (
          <ColocaBadge variant="ghost" className="shrink-0 border border-border">
            {t("access_extra", { count: rest.length })}
          </ColocaBadge>
        )}
      </span>
      {summary && (
        <span className="font-geist-mono text-[11px] tabular-nums text-muted-foreground">
          {t("access_tally", { allowed: summary.allowed, total: summary.total })}
        </span>
      )}
    </button>
  );
}
